import { useEffect, useRef, useState } from 'react';
import { isChunkLoadFailure, StaleBuildError } from '../errors';

/**
 * Renders Mermaid text the API generated from the process rows (D-003: the browser renders;
 * the server never runs a headless browser). securityLevel 'strict' encodes any HTML in
 * labels — a second line of defence behind the API's own escaping.
 *
 * mermaid is loaded lazily: it is ~2 MB and only the Diagram tab needs it.
 */

let initialised = false;
let counter = 0;

/** The one Mermaid configuration. web/scripts/check-mermaid-parse.mjs renders with the same object. */
export const MERMAID_CONFIG = { startOnLoad: false, securityLevel: 'strict', theme: 'base', htmlLabels: false, flowchart: { htmlLabels: false } } as const;

async function mermaidReady() {
  let mermaid;
  try {
    mermaid = (await import('mermaid')).default;
  } catch (e) {
    // The chunk 404s when this page was built before the current deploy (D-059). That is not a
    // broken diagram and must not be reported as one: the page is stale and only a reload fixes it.
    if (isChunkLoadFailure(e)) throw new StaleBuildError(e instanceof Error ? e.message : String(e));
    throw e;
  }
  if (!initialised) {
    // htmlLabels off — at BOTH levels. Mermaid 11 reads the top-level flag for node labels and the
    // flowchart flag for clusters/edges; with only one set, step names still render as
    // <foreignObject> HTML, which the publish sanitiser strips (2026-09-09 incident, BUILD.md §14).
    // Labels as SVG <text> survive sanitising and embed in the PDF (D-003).
    mermaid.initialize({ ...MERMAID_CONFIG, fontFamily: 'Montserrat, Arial, sans-serif' });
    initialised = true;
  }
  return mermaid;
}

/** Presentation properties Mermaid sets through CSS classes; the PDF renderer only reads attributes. */
const INLINE_PROPS = ['fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-dasharray', 'stroke-opacity', 'opacity', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-anchor', 'dominant-baseline'] as const;

/**
 * Copies each element's computed style onto attributes and drops the <style> blocks, so the
 * SVG means the same thing without CSS. Standard SVG-export technique; without it the PDF
 * showed every shape in default black (2026-09-09). The element must be in the document for
 * getComputedStyle to resolve the cascade, so it is attached off-screen briefly.
 */
export function inlineSvgStyles(svg: string): string {
  const host = document.createElement('div');
  host.style.cssText = 'position:absolute;left:-100000px;top:0;width:1px;height:1px;overflow:hidden;';
  host.innerHTML = svg;
  document.body.appendChild(host);
  try {
    const root = host.querySelector('svg');
    if (!root) return svg;
    const all = [root, ...Array.from(root.querySelectorAll<SVGElement>('*'))].filter((el) => el.tagName.toLowerCase() !== 'style');
    // Pass 1: read every computed style while the classes are still in place. Removing a class
    // as we go would break descendant selectors such as ".cluster rect" for the children.
    const computed = all.map((el) => {
      const cs = window.getComputedStyle(el);
      const out: Array<[string, string]> = [];
      for (const p of INLINE_PROPS) {
        if (el.hasAttribute(p)) continue;
        let v = cs.getPropertyValue(p);
        if (!v) continue;
        if (p === 'stroke-dasharray' && !usableDashArray(v)) continue;
        out.push([p, v]);
      }
      return out;
    });
    // Pass 2: write attributes and drop the classes.
    all.forEach((el, i) => {
      for (const [p, v] of computed[i]) el.setAttribute(p, v);
      el.removeAttribute('class');
      if (el !== root && el.getAttribute('style')) el.setAttribute('style', dropZeroDash(el.getAttribute('style') ?? ''));
    });
    for (const s of Array.from(root.querySelectorAll('style'))) s.remove();
    // Give the root explicit dimensions from its viewBox; percentages mean nothing in a PDF.
    const vb = root.getAttribute('viewBox')?.split(/[\s,]+/).map(Number);
    if (vb && vb.length === 4) {
      root.setAttribute('width', String(vb[2]));
      root.setAttribute('height', String(vb[3]));
      root.removeAttribute('style');
    }
    return new XMLSerializer().serializeToString(root);
  } finally {
    host.remove();
  }
}

/** "none", or any pattern with a zero or negative length (Mermaid emits "1, 0" for solid lines), is not a dash pattern. */
function usableDashArray(v: string): boolean {
  if (v === 'none') return false;
  const nums = v.split(/[\s,]+/).filter(Boolean).map((n) => parseFloat(n));
  return nums.length > 0 && nums.every((n) => Number.isFinite(n) && n > 0);
}
function dropZeroDash(style: string): string {
  return style.replace(/stroke-dasharray\s*:\s*([^;]+);?/gi, (m, v) => (usableDashArray(String(v).trim()) ? m : ''));
}

/** Renders Mermaid text to a self-contained SVG string — what the browser sends when publishing. */
export async function renderSvg(source: string): Promise<string> {
  const mermaid = await mermaidReady();
  const { svg } = await mermaid.render(`ph-export-${++counter}`, source);
  return inlineSvgStyles(svg);
}

export function MermaidDiagram({ source }: { source: string }) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setStale(false);
    (async () => {
      try {
        // Inside the try: loading Mermaid is the step most likely to fail, because it is the one
        // lazily fetched chunk. It sat outside, so a 404 on it rejected this function with nothing
        // catching it — an unhandled rejection, no error state, and an empty panel that looked
        // like the diagram had silently stopped working (D-059).
        const mermaid = await mermaidReady();
        const { svg } = await mermaid.render(`ph-diagram-${++counter}`, source);
        if (!cancelled && host.current) host.current.innerHTML = svg;
      } catch (e) {
        if (cancelled) return;
        if (e instanceof StaleBuildError) setStale(true);
        else setError(e instanceof Error ? e.message : 'The diagram could not be rendered.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (stale)
    return (
      <div className="notice" data-testid="diagram-stale">
        {new StaleBuildError('chunk').message}{' '}
        <button type="button" className="link-button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  if (error) return <div className="notice notice--error">The diagram could not be rendered: {error}</div>;
  return <div ref={host} className="diagram" data-testid="diagram" />;
}
